/**
 * Biçimlendirme — sunucudan gelen ham değerleri insanın okuduğu hâle çevirir.
 *
 * Para kuralı (CLAUDE.md kural 4): sunucu tutarları **string** gönderir
 * ("1260.00"). Burada `Number()`'a çevirmek kuruşta kayma riski taşır, ama
 * `Intl.NumberFormat` sayı ister. Çözüm: dönüştürme yalnızca GÖSTERİM için
 * yapılır ve asla geri yazılmaz. Hesap hiçbir zaman tarayıcıda yapılmaz —
 * toplamları sunucu hesaplar, panel yalnızca gösterir.
 */

const paraBicimi = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/*
 * Saat dilimi SABİT Türkiye: fatura Türkiye saatiyle kesilir, panel de o
 * saati gösterir — tarayıcı nerede olursa olsun. Ölçülen kusur: `tr-TR` dili
 * seçiyordu, saat dilimini değil; UTC istemcide 2 Eylül 01:30'daki olay
 * 1 Eylül 22:30 görünüyordu.
 */
const TR = 'Europe/Istanbul';

const tarihBicimi = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: TR,
});

const tarihSaatBicimi = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: TR,
});

const uzunTarihBicimi = new Intl.DateTimeFormat('tr-TR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: TR,
});

/**
 * Yalnız-tarih (`2026-09-02`) değerleri öğlen UTC'ye sabitlenir: Türkiye
 * saatine çevrildiğinde gün hiçbir istemcide kaymaz.
 */
function tarihNesnesi(iso: string): Date {
  return new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
}

/** "1260.00" → "1.260,00" (para birimi ayrı gösterilir, hizalama bozulmasın). */
export function paraYaz(tutar: string | null | undefined): string {
  if (tutar === null || tutar === undefined || tutar === '') return '—';
  const sayi = Number(tutar);
  if (!Number.isFinite(sayi)) return tutar;
  return paraBicimi.format(sayi);
}

/** "20.00" → "%20" — sondaki gereksiz sıfırlar atılır. */
export function oranYaz(oran: string | null | undefined): string {
  if (!oran) return '—';
  const sayi = Number(oran);
  if (!Number.isFinite(sayi)) return oran;
  return `%${paraBicimi.format(sayi).replace(/,00$/, '')}`;
}

/** "3.0000" → "3" ; "1.5000" → "1,5" */
export function adetYaz(adet: string | null | undefined): string {
  if (!adet) return '—';
  const sayi = Number(adet);
  if (!Number.isFinite(sayi)) return adet;
  return new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 4 }).format(sayi);
}

export function tarihYaz(iso: string | null | undefined): string {
  if (!iso) return '—';
  const tarih = tarihNesnesi(iso);
  return Number.isNaN(tarih.getTime()) ? '—' : tarihBicimi.format(tarih);
}

export function tarihSaatYaz(iso: string | null | undefined): string {
  if (!iso) return '—';
  const tarih = new Date(iso);
  return Number.isNaN(tarih.getTime()) ? '—' : tarihSaatBicimi.format(tarih);
}

export function uzunTarihYaz(iso: string | null | undefined): string {
  if (!iso) return '—';
  const tarih = tarihNesnesi(iso);
  return Number.isNaN(tarih.getTime()) ? '—' : uzunTarihBicimi.format(tarih);
}

/** "2 dakika önce" — olay geçmişinde mutlak tarihin yanında okunurluk sağlar. */
export function goreliZaman(iso: string | null | undefined): string {
  if (!iso) return '';
  const gecen = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(gecen)) return '';

  const saniye = Math.round(gecen / 1000);
  if (saniye < 60) return 'az önce';
  const dakika = Math.round(saniye / 60);
  if (dakika < 60) return `${dakika} dakika önce`;
  const saat = Math.round(dakika / 60);
  if (saat < 24) return `${saat} saat önce`;
  const gun = Math.round(saat / 24);
  if (gun < 30) return `${gun} gün önce`;
  return uzunTarihYaz(iso);
}

/** Belge tipi etiketi — arayüzde "EARCHIVE" gibi kod GÖRÜNMEZ. */
export function belgeTipiYaz(kind: string): string {
  return kind === 'EARCHIVE' ? 'e-Arşiv' : 'e-Fatura';
}

export function yonYaz(direction: string): string {
  return direction === 'CREDIT_NOTE' ? 'İade' : 'Satış';
}

/** VKN/TCKN ayrımı — 10 hane VKN, 11 hane TCKN. */
export function kimlikTipiYaz(tip: string | null | undefined): string {
  if (tip === 'VKN') return 'VKN';
  if (tip === 'TCKN') return 'TCKN';
  return 'Kimlik';
}
